import { useCallback, useEffect, useRef, useState } from "react";
import {
  ASSESSMENT_PHOTO_ATTESTATION_SV,
  ASSESSMENT_PHOTO_JOURNAL_HEADING,
  ASSESSMENT_PHOTO_REASON_MAX,
  ASSESSMENT_PHOTO_REVIEW_OUTCOME_LABELS,
  assessmentPhotoJournalText,
  assessmentPhotoViewLabel,
  type AssessmentPhotoReviewOutcome,
  type AssessmentPhotoReviewStatus,
  type ClinicianAssessmentPhotoRound,
  type ClinicianAssessmentPhotosView,
} from "@care-by-ella/contracts";
import { useAuth } from "@/auth/AuthContext";
import {
  fetchAssessmentPhotos,
  fetchPatientPhotoBlob,
  reviewAssessmentPhotos,
  UnauthorizedError,
  ValidationError,
} from "@/lib/clinician-api";
import { useReadOnly } from "./SupportView";

// TES-1648 (Ö-1016) — "Patientbilder" on the patient overview: the written
// track's front, side and back photos together, with the date, her
// attestation and the review status, and the doctor's "Godkänn bilder",
// "Begär nya bilder" or "Kräv vårdmöte" with a short patient-facing reason
// (F02 "Läkar-admin"). A positive written decision is refused until the
// photos are approved; this panel is where they get approved.
//
// Doctor only, outside the admin's read-only view: "Endast läkare och annan
// uttryckligen behörig vårdpersonal får se bilderna. Coachen ska inte ha
// åtkomst." Renders nothing for a patient the task has never applied to.
// The status lines are proposed staff copy; the button labels are F02's.

const OUTCOMES: AssessmentPhotoReviewOutcome[] = [
  "approve",
  "retake",
  "require_meeting",
];

const OUTCOME_OF_STATUS: Partial<
  Record<AssessmentPhotoReviewStatus, AssessmentPhotoReviewOutcome>
> = {
  approved: "approve",
  retake_required: "retake",
  meeting_required: "require_meeting",
};

function formatDay(iso: string): string {
  return new Date(iso).toLocaleDateString("sv-SE", {
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: "Europe/Stockholm",
  });
}

function statusLine(round: ClinicianAssessmentPhotoRound | undefined): string {
  if (!round) return "Bilderna saknas.";
  const by = round.reviewedByName ? ` av ${round.reviewedByName}` : "";
  const at = round.reviewedAt ? ` ${formatDay(round.reviewedAt)}` : "";
  switch (round.reviewStatus) {
    case null:
      return `Patienten har börjat ladda upp (${round.photos.length} av 3).`;
    case "pending":
      return `Inskickade ${round.submittedAt ? formatDay(round.submittedAt) : ""} – väntar på granskning.`;
    case "approved":
      return `Godkända${at}${by}.`;
    case "retake_required":
      return `Nya bilder begärda${at}${by}.`;
    case "meeting_required":
      return `Vårdmöte krävt i stället${at}${by}.`;
  }
}

function RoundPhotos({
  patientId,
  round,
  onUnauthorized,
}: {
  patientId: string;
  round: ClinicianAssessmentPhotoRound;
  onUnauthorized: () => void;
}) {
  const [urls, setUrls] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const made: string[] = [];
    void (async () => {
      try {
        const next: Record<string, string> = {};
        for (const p of round.photos) {
          const url = URL.createObjectURL(
            await fetchPatientPhotoBlob(patientId, p.photoId),
          );
          made.push(url);
          next[p.photoId] = url;
        }
        if (!cancelled) setUrls(next);
      } catch (e) {
        if (cancelled) return;
        if (e instanceof UnauthorizedError) {
          onUnauthorized();
          return;
        }
        setError("Bilderna kunde inte hämtas.");
      }
    })();
    return () => {
      cancelled = true;
      made.forEach((u) => URL.revokeObjectURL(u));
    };
  }, [patientId, round, onUnauthorized]);

  return (
    <div className="flex flex-col gap-xs">
      {error ? (
        <p className="text-xs text-danger" role="alert">
          {error}
        </p>
      ) : null}
      <ul className="grid grid-cols-3 gap-sm">
        {round.photos.map((p) => (
          <li key={p.photoId} className="flex flex-col gap-xxs text-xs">
            <span className="font-medium">
              {assessmentPhotoViewLabel(p.view)}
            </span>
            {urls[p.photoId] ? (
              <a href={urls[p.photoId]} target="_blank" rel="noreferrer">
                <img
                  src={urls[p.photoId]}
                  alt={`Patientbild ${assessmentPhotoViewLabel(p.view).toLowerCase()}`}
                  className="max-h-[320px] w-full rounded-sm border border-surfaceMuted object-contain"
                />
              </a>
            ) : (
              <span className="text-textSecondary">Hämtar…</span>
            )}
            <span className="text-textSecondary">
              Uppladdad {formatDay(p.uploadedAt)}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function AssessmentPhotosPanel({
  patientId,
  onUnauthorized,
}: {
  patientId: string;
  onUnauthorized: () => void;
}) {
  const { claims } = useAuth();
  const readOnly = useReadOnly();
  const [view, setView] = useState<ClinicianAssessmentPhotosView | null>(null);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const onUnauthorizedRef = useRef(onUnauthorized);
  onUnauthorizedRef.current = onUnauthorized;
  const stableUnauthorized = useCallback(() => onUnauthorizedRef.current(), []);
  const isDoctor = claims?.role === "doctor" && !readOnly;

  const load = useCallback(async () => {
    try {
      setView(await fetchAssessmentPhotos(patientId));
    } catch (e) {
      if (e instanceof UnauthorizedError) onUnauthorizedRef.current();
      // Otherwise nothing renders: the panel shows only what the server says.
    }
  }, [patientId]);

  useEffect(() => {
    if (isDoctor) void load();
  }, [isDoctor, load]);

  if (!isDoctor || !view) return null;
  if (!view.applies && view.rounds.length === 0) return null;

  const current = view.rounds[0];
  const earlier = view.rounds.slice(1);
  const reviewedOutcome = current?.reviewStatus
    ? OUTCOME_OF_STATUS[current.reviewStatus]
    : undefined;
  const journalText =
    current && reviewedOutcome
      ? assessmentPhotoJournalText({
          outcome: reviewedOutcome,
          submittedAt: current.submittedAt,
          attestedAt: current.attestedAt,
          reason: current.patientVisibleReason,
        })
      : null;

  async function submit(outcome: AssessmentPhotoReviewOutcome) {
    if (!current) return;
    const trimmed = reason.trim();
    if (outcome === "retake" && !trimmed) {
      setError("Skriv kort till patienten vad som behöver tas om.");
      return;
    }
    if (
      outcome === "require_meeting" &&
      !window.confirm(
        "Kräv vårdmöte? Patienten får ett kort i appen och en pushnotis om att boka ett vårdmöte.",
      )
    ) {
      return;
    }
    setBusy(true);
    setError(null);
    try {
      setView(
        await reviewAssessmentPhotos(patientId, {
          roundId: current.id,
          outcome,
          ...(trimmed ? { reason: trimmed } : {}),
        }),
      );
      setReason("");
    } catch (e) {
      if (e instanceof UnauthorizedError) {
        onUnauthorized();
        return;
      }
      setError(
        e instanceof ValidationError && e.issues[0]?.message
          ? e.issues[0].message
          : "Granskningen kunde inte sparas. Försök igen.",
      );
      await load();
    } finally {
      setBusy(false);
    }
  }

  async function copyJournal() {
    if (!journalText) return;
    try {
      await navigator.clipboard.writeText(journalText);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  }

  return (
    <section className="bg-surface border border-surfaceMuted rounded-md p-md">
      <h2 className="font-display text-lg mb-xs">Patientbilder</h2>
      <div className="flex flex-col gap-sm text-sm">
        <p role="status">{statusLine(current)}</p>
        {view.applies && !view.requiredForDecision ? (
          <p className="text-textSecondary">
            Patientens app kan inte ta emot bilduppgiften ännu. Ett positivt
            skriftligt beslut kräver därför inte bilder för henne.
          </p>
        ) : null}
        {view.requiredForDecision && current?.reviewStatus !== "approved" ? (
          <p className="text-textSecondary">
            Ett positivt skriftligt behandlingsbeslut kräver godkända bilder.
            Ett negativt beslut, en komplettering eller ett vårdmöte kräver det
            inte.
          </p>
        ) : null}

        {current && current.photos.length > 0 ? (
          <RoundPhotos
            key={current.id}
            patientId={patientId}
            round={current}
            onUnauthorized={stableUnauthorized}
          />
        ) : null}
        {current?.attestedAt ? (
          <p className="text-xs text-textSecondary">
            Patienten har intygat {formatDay(current.attestedAt)}: ”
            {ASSESSMENT_PHOTO_ATTESTATION_SV}”
          </p>
        ) : null}
        {current?.patientVisibleReason ? (
          <p className="text-xs">
            Orsak till patienten: {current.patientVisibleReason}
          </p>
        ) : null}

        {view.canReview ? (
          <div className="flex flex-col gap-xs">
            <label className="flex flex-col gap-xxs text-xs">
              Kort orsak till patienten (krävs vid ”Begär nya bilder”)
              <textarea
                className="rounded-sm border border-surfaceMuted p-xs text-sm"
                rows={2}
                maxLength={ASSESSMENT_PHOTO_REASON_MAX}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              />
            </label>
            <div className="flex flex-wrap gap-xs">
              {OUTCOMES.map((outcome) => (
                <button
                  key={outcome}
                  type="button"
                  className={
                    outcome === "approve"
                      ? "rounded-md bg-brand px-md py-xs text-sm text-white hover:opacity-90 disabled:opacity-50"
                      : "rounded-md border border-surfaceMuted px-md py-xs text-sm hover:border-brand disabled:opacity-50"
                  }
                  disabled={busy}
                  onClick={() => void submit(outcome)}
                >
                  {ASSESSMENT_PHOTO_REVIEW_OUTCOME_LABELS[outcome]}
                </button>
              ))}
            </div>
          </div>
        ) : null}
        {error ? (
          <p className="text-xs text-danger" role="alert">
            {error}
          </p>
        ) : null}

        {journalText ? (
          <div className="flex flex-col gap-xxs">
            <span className="text-xs font-medium">
              Journaltext – {ASSESSMENT_PHOTO_JOURNAL_HEADING}
            </span>
            <pre className="whitespace-pre-wrap rounded-sm bg-surfaceMuted p-xs text-xs">
              {journalText}
            </pre>
            <div>
              <button
                type="button"
                className="text-xs text-brand underline"
                onClick={() => void copyJournal()}
              >
                {copied ? "Kopierad" : "Kopiera journaltext"}
              </button>
            </div>
          </div>
        ) : null}

        {earlier.length > 0 ? (
          <details className="text-xs">
            <summary className="cursor-pointer">
              Tidigare omgångar ({earlier.length})
            </summary>
            <ul className="mt-xs flex flex-col gap-xxs">
              {earlier.map((r) => (
                <li key={r.id}>
                  {statusLine(r)}
                  {r.patientVisibleReason
                    ? ` Orsak: ${r.patientVisibleReason}`
                    : ""}
                </li>
              ))}
            </ul>
          </details>
        ) : null}
      </div>
    </section>
  );
}
